import React, { useEffect, useMemo, useState } from 'react';
import construccionCivilService from '../services/construccionCivilService';

const CATEGORIAS = [
  { id: 'operario', nombre: 'Operario', color: 'teal' },
  { id: 'oficial', nombre: 'Oficial', color: 'blue' },
  { id: 'peon', nombre: 'Peón', color: 'amber' },
];

const FORM_VACIO = {
  categoria: 'operario',
  vigente_desde: '2026-01-01',
  vigente_hasta: '2026-12-31',
  fuente_normativa: 'RM 197-2025-TR',
  jornal_basico: '',
  buc_porcentaje: '32',
  movilidad_dia: '8.60',
  motivo: 'Actualización de convenio',
};

const COLORES = {
  teal: { badge: 'bg-teal-100 text-teal-800', monto: 'text-teal-600', ring: 'border-teal-200' },
  blue: { badge: 'bg-blue-100 text-blue-800', monto: 'text-blue-600', ring: 'border-blue-200' },
  amber: { badge: 'bg-amber-100 text-amber-800', monto: 'text-amber-700', ring: 'border-amber-200' },
};

function hoyIso() {
  return new Date().toISOString().slice(0, 10);
}

function fechaIso(valor) {
  return valor ? String(valor).slice(0, 10) : '';
}

function formatearFecha(valor) {
  const iso = fechaIso(valor);
  if (!iso) return 'Abierta';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function formatearMonto(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '—';
  return `S/ ${n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function bucComoPorcentaje(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '—';
  const pct = n <= 1 ? n * 100 : n;
  return `${pct.toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
}

function promedioBucParaFormulario(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '';
  const pct = n <= 1 ? n * 100 : n;
  return String(pct);
}

function estaVigente(parametro, dia = hoyIso()) {
  const desde = fechaIso(parametro.vigente_desde);
  const hasta = parametro.vigente_hasta ? fechaIso(parametro.vigente_hasta) : '9999-12-31';
  return desde && dia >= desde && dia <= hasta;
}

function nombreCategoria(id) {
  return CATEGORIAS.find((c) => c.id === id)?.nombre || id;
}

function colorCategoria(id) {
  return COLORES[CATEGORIAS.find((c) => c.id === id)?.color || 'teal'];
}

const ConstruccionCivilPanel = () => {
  const [parametros, setParametros] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [errores, setErrores] = useState({});
  const [param, setParam] = useState(FORM_VACIO);

  const cargar = async () => {
    setCargando(true);
    const res = await construccionCivilService.parametrosGlobales();
    if (res.success) {
      setParametros(res.data || []);
    } else {
      setMensaje({ tipo: 'error', texto: res.error || 'No se pudieron cargar los parámetros.' });
    }
    setCargando(false);
  };

  useEffect(() => { cargar(); }, []);

  const vigentesPorCategoria = useMemo(() => {
    const dia = hoyIso();
    return CATEGORIAS.reduce((acc, cat) => {
      acc[cat.id] = (parametros || [])
        .filter((p) => p.categoria === cat.id && estaVigente(p, dia))
        .sort((a, b) => fechaIso(b.vigente_desde).localeCompare(fechaIso(a.vigente_desde)))[0] || null;
      return acc;
    }, {});
  }, [parametros]);

  const abrirModal = (categoria = 'operario') => {
    const vigente = vigentesPorCategoria[categoria];
    setParam({
      ...FORM_VACIO,
      categoria,
      jornal_basico: vigente ? String(Number(vigente.jornal_basico)) : '',
      buc_porcentaje: vigente ? promedioBucParaFormulario(vigente.buc_porcentaje) : (categoria === 'operario' ? '32' : '30'),
      movilidad_dia: vigente ? String(Number(vigente.movilidad_dia)) : '8.60',
      fuente_normativa: vigente?.fuente_normativa || FORM_VACIO.fuente_normativa,
    });
    setErrores({});
    setModalAbierto(true);
  };

  const validar = () => {
    const next = {};
    if (!param.categoria) next.categoria = 'Selecciona una categoría';
    if (!param.vigente_desde) next.vigente_desde = 'Indica el inicio de vigencia';
    if (param.vigente_hasta && param.vigente_hasta < param.vigente_desde) {
      next.vigente_hasta = 'La fecha final no puede ser anterior al inicio';
    }
    if (!param.fuente_normativa.trim()) next.fuente_normativa = 'Indica la resolución o convenio';
    if (!param.jornal_basico || Number(param.jornal_basico) <= 0) next.jornal_basico = 'Ingresa el jornal diario';
    if (!param.buc_porcentaje || Number(param.buc_porcentaje) <= 0) next.buc_porcentaje = 'Ingresa el BUC';
    if (!param.movilidad_dia || Number(param.movilidad_dia) < 0) next.movilidad_dia = 'Ingresa la movilidad diaria';
    if (!param.motivo.trim()) next.motivo = 'Indica el motivo del cambio';
    setErrores(next);
    return Object.keys(next).length === 0;
  };

  const guardarParametro = async (e) => {
    e.preventDefault();
    if (!validar()) return;
    setGuardando(true);
    const buc = Number(param.buc_porcentaje);
    const res = await construccionCivilService.guardarParametroGlobal({
      ...param,
      jornal_basico: Number(param.jornal_basico),
      buc_porcentaje: buc > 1 ? buc / 100 : buc,
      movilidad_dia: Number(param.movilidad_dia),
    });
    setGuardando(false);
    if (res.success) {
      setMensaje({ tipo: 'ok', texto: 'Nueva vigencia registrada. Las planillas cerradas no cambian.' });
      setModalAbierto(false);
      cargar();
    } else {
      setMensaje({ tipo: 'error', texto: res.error || 'No se pudo guardar el parámetro.' });
    }
  };

  const campo = (nombre) => (
    `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
      errores[nombre] ? 'border-red-500 bg-red-50' : 'border-gray-300'
    }`
  );

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Construcción civil</h3>
          <p className="text-sm text-gray-600">
            Jornales CONSU por categoría. Se aplican a todas las empresas cuando el trabajador tiene el régimen asignado.
          </p>
        </div>
        <button
          type="button"
          onClick={() => abrirModal('operario')}
          className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nueva vigencia</span>
        </button>
      </div>

      {mensaje && (
        <div className={`p-4 rounded-lg border text-sm ${
          mensaje.tipo === 'ok'
            ? 'bg-teal-50 border-teal-200 text-teal-800'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {mensaje.texto}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        {CATEGORIAS.map((cat) => {
          const vigente = vigentesPorCategoria[cat.id];
          const paleta = COLORES[cat.color];
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => abrirModal(cat.id)}
              className={`text-left rounded-xl border ${paleta.ring} bg-white p-5 hover:shadow-sm transition-shadow`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${paleta.badge}`}>
                  {cat.nombre}
                </span>
                <span className={`text-xs font-medium ${vigente ? 'text-green-700' : 'text-gray-400'}`}>
                  {vigente ? 'Vigente' : 'Sin vigencia'}
                </span>
              </div>
              <p className={`text-2xl font-bold ${paleta.monto}`}>
                {vigente ? formatearMonto(vigente.jornal_basico) : '—'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Jornal diario</p>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">BUC</p>
                  <p className="font-medium text-gray-900">{vigente ? bucComoPorcentaje(vigente.buc_porcentaje) : '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Movilidad</p>
                  <p className="font-medium text-gray-900">{vigente ? formatearMonto(vigente.movilidad_dia) : '—'}</p>
                </div>
              </div>
              {vigente && (
                <p className="mt-3 text-xs text-gray-500">
                  {formatearFecha(vigente.vigente_desde)} – {formatearFecha(vigente.vigente_hasta)}
                </p>
              )}
            </button>
          );
        })}
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Historial de vigencias</h4>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {cargando ? (
            <div className="p-8 text-center text-sm text-gray-500">Cargando parámetros…</div>
          ) : parametros.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              Aún no hay jornales registrados. Usa «Nueva vigencia» para cargar el convenio.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoría</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jornal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">BUC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Movilidad</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vigencia</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fuente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {parametros.map((p) => {
                  const vigente = estaVigente(p);
                  const paleta = colorCategoria(p.categoria);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${paleta.badge}`}>
                          {nombreCategoria(p.categoria)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {formatearMonto(p.jornal_basico)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {bucComoPorcentaje(p.buc_porcentaje)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatearMonto(p.movilidad_dia)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatearFecha(p.vigente_desde)} – {formatearFecha(p.vigente_hasta)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                        <span className="line-clamp-2">{p.fuente_normativa}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          vigente ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {vigente ? 'Vigente' : 'Histórico'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 text-sm text-blue-800">
            <p className="font-medium">Cómo se usa</p>
            <ul className="mt-2 list-disc list-inside space-y-1 text-blue-700">
              <li>El trabajador CONSU recibe operario, oficial o peón en su ficha.</li>
              <li>La planilla toma el jornal vigente a la fecha de cada día, no un valor fijo en el código.</li>
              <li>Una nueva vigencia no modifica planillas ya cerradas.</li>
            </ul>
          </div>
        </div>
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Nueva vigencia</h3>
                <p className="text-sm text-gray-500">Registra un jornal sin alterar planillas cerradas</p>
              </div>
              <button type="button" onClick={() => setModalAbierto(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <form onSubmit={guardarParametro} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                <select className={campo('categoria')} value={param.categoria} onChange={(e) => setParam({ ...param, categoria: e.target.value })}>
                  {CATEGORIAS.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vigente desde</label>
                  <input type="date" className={campo('vigente_desde')} value={param.vigente_desde} onChange={(e) => setParam({ ...param, vigente_desde: e.target.value })} />
                  {errores.vigente_desde && <p className="mt-1 text-xs text-red-600">{errores.vigente_desde}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vigente hasta</label>
                  <input type="date" className={campo('vigente_hasta')} value={param.vigente_hasta} onChange={(e) => setParam({ ...param, vigente_hasta: e.target.value })} />
                  {errores.vigente_hasta && <p className="mt-1 text-xs text-red-600">{errores.vigente_hasta}</p>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jornal (S/)</label>
                  <input type="number" step="0.01" min="0" placeholder="89.30" className={campo('jornal_basico')} value={param.jornal_basico} onChange={(e) => setParam({ ...param, jornal_basico: e.target.value })} />
                  {errores.jornal_basico && <p className="mt-1 text-xs text-red-600">{errores.jornal_basico}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BUC (%)</label>
                  <input type="number" step="0.01" min="0" placeholder="32" className={campo('buc_porcentaje')} value={param.buc_porcentaje} onChange={(e) => setParam({ ...param, buc_porcentaje: e.target.value })} />
                  <p className="mt-1 text-xs text-gray-500">32 = 32%</p>
                  {errores.buc_porcentaje && <p className="mt-1 text-xs text-red-600">{errores.buc_porcentaje}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Movilidad (S/)</label>
                  <input type="number" step="0.01" min="0" placeholder="8.60" className={campo('movilidad_dia')} value={param.movilidad_dia} onChange={(e) => setParam({ ...param, movilidad_dia: e.target.value })} />
                  {errores.movilidad_dia && <p className="mt-1 text-xs text-red-600">{errores.movilidad_dia}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fuente normativa</label>
                <input className={campo('fuente_normativa')} value={param.fuente_normativa} onChange={(e) => setParam({ ...param, fuente_normativa: e.target.value })} placeholder="RM 197-2025-TR" />
                {errores.fuente_normativa && <p className="mt-1 text-xs text-red-600">{errores.fuente_normativa}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                <input className={campo('motivo')} value={param.motivo} onChange={(e) => setParam({ ...param, motivo: e.target.value })} placeholder="Actualización de convenio" />
                {errores.motivo && <p className="mt-1 text-xs text-red-600">{errores.motivo}</p>}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModalAbierto(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg" disabled={guardando}>
                  Cancelar
                </button>
                <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50" disabled={guardando}>
                  {guardando ? 'Guardando…' : 'Guardar vigencia'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConstruccionCivilPanel;
